import {AvailablePromoCode} from "@/base/types/AvailablePromoCode";
import {useTranslation} from "next-i18next";
import {useUser} from "@/base/components/auth/UserProvider";
import {useNotification} from "@/base/components/NotificationProvider";
import {useRouter} from "next/router";
import React, {useState} from "react";
import {useTheme} from "@/base/components/theme/BaseThemeProvider";
import SimpleCard from "@/base/components/dashboard/SimpleCart";
import {Button, Tooltip} from "antd";
import InfoCircleOutlined from "@ant-design/icons/InfoCircleOutlined";
import {FaExternalLinkAlt} from "react-icons/fa";
import {BiRocket} from "react-icons/bi";

export const AvailablePromoCard = ({className, promo}: { className?: string, promo: AvailablePromoCode }) => {
	const {t} = useTranslation();
	const UserContext = useUser();
	const [api] = useNotification();
	const router = useRouter();
	const [refreshPromosState, setRefreshPromosState] = useState<number>(-1);
	const {theme} = useTheme();

	const current = promo.progress?.current || 0;
	const max = promo.progress?.max || 0;
	// calculate percentage
	const percentage = Math.round(current / max * 100);
	const isPercentage = max >= 100;

	const [loading, setLoading] = useState<boolean>(false);

	const onActivatePromo = async (promo: AvailablePromoCode) => {
		if (!promo.active_promo_link || loading) return;

		if (promo.active_promo_link.includes("/activate/"))
		{
			// use fetch

			setLoading(true);

			return UserContext.Fetch({
				url: promo.active_promo_link,
				method: 'GET',
			}).then((res) => {
				return UserContext.updateUser().finally(() => api.success({
					message: t('base-dashboard-promo:available.activated')
				})).then(() => {
					setRefreshPromosState(Math.random());
				});
			}).catch(err => {
				api.error({
					message: err.message || err
				});
			}).finally(() => setLoading(false));
		}

		router.push(promo.active_promo_link).catch(err => {
			api.error({
				message: err.message || err
			});
		});
	}

	return (
		<SimpleCard
			className={`rounded-2x overflow-hidden shadow-[0px_4px_3px_2px_rgba(233,235,244,0.5)] border-solid border-gray-400/30 border-[0.5px] bg-no-repeat ${className}`}
			style={{
				backgroundImage: `url(/base/image/gift-blue.png)`,
				backgroundSize: '170px',
				backgroundPosition: 'right bottom',
				...(theme == 'dark' && {
					backgroundColor: 'rgba(0,0,0,0.8)',
					backgroundBlendMode: 'overlay',
				})
			}}
			bodyClassName={` gap-5 flex relative h-full overflow-hidden flex-row`}
			headerClassName={"!pb-6"}
		>
			<div className={`grid xl:grid-cols-3`}>
				<div className={`flex flex-col justify-between h-full gap-2.5 xl:col-span-2`}>
					<div className={`flex gap-4 h-full`}>
						<div className={`flex flex-col gap-2.5 basis-[fit-content]`}>
							<h3 className={`font-montserrat text-text-2 text-2xl font-semibold`}>
								{promo.name}
							</h3>

							{promo.description && <p>
								{promo.description}
							</p>}
						</div>
					</div>

					{!isNaN(percentage) && (
						<Tooltip className={`flex items-center gap-4 ${promo.progress?.info && `cursor-help`}`} title={promo.progress?.info} placement={`bottom`}>
							<div className={`flex flex-grow flex-col gap-1 w-full mt-2`}>
								<div className={`h-[13px] bg-gray-200 dark:bg-gray-400 overflow-hidden rounded-xl`}>
									<div className={`bg-primary h-full`} style={{
										width: `${percentage}%`,
									}}></div>
								</div>
								<p className={`font-extrabold font-montserrat-alternates text-primary ms-auto`}>{isPercentage ? `% ` : `${current} / `}{max}</p>
							</div>
							{promo.progress?.info && (
								<div>
									<InfoCircleOutlined size={30}/>
								</div>
							)}
						</Tooltip>
					)}

					<div className={`flex flex-col justify-center lg:justify-start w-full`}>
						{promo?.active_promo_link ? (
							<>
								<Button
									className={`!shadow-none max-lg:w-full max-w-[300px]`}
									type={'primary'}
									href={promo?.active_promo_link?.toString()?.includes("/activate/") ? undefined : promo?.active_promo_link?.toString().replace("&need_contact=true", "").replace("?need_contact=true", "")}
									target={`_blank`}
									onClick={() => onActivatePromo(promo)}
									loading={loading}
								>
									{promo?.active_promo_link?.toString()?.includes("/activate/") ? t('base-dashboard-promo:available.activate') : (
										promo?.active_promo_link?.toString()?.includes("need_contact=true") ? (
											<span>
													{t('base-dashboard-promo:available.start')}
												<BiRocket className={`ms-2`}/>
												</span>
										) : (
											<span>
													{t('base-dashboard-promo:available.link')}
												<FaExternalLinkAlt className={`ms-2`}/>
												</span>
										)
									)}
								</Button>
								{promo?.active_promo_link?.toString()?.includes("need_contact=true") && (
									<>
										{/*<hr className={`w-full opacity-20`}/>*/}
										<p className={`text-start text-sm mt-2`}>
											{t('base-dashboard-promo:available.need-contact')}
										</p>
									</>
								)}
							</>
						) : null}
					</div>
				</div>
			</div>
		</SimpleCard>
	)
}